/**
 * @fileoverview Application header component with collapsible menu toggle and
 * customisable slots for title and toolbar actions.
 */

import * as React from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { TooltipIconButton } from '@/shared/components';
import { SidebarExpansionContext } from './SidebarExpansionContext';

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: "solid",
  borderColor: (theme.vars ?? theme).palette.divider,
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
}));

export interface AppHeaderSlotProps {
  appTitle?: Record<string, unknown>;
  toolbarActions?: Record<string, unknown>;
}

export interface AppHeaderSlots {
  appTitle?: React.ElementType;
  toolbarActions?: React.JSXElementConstructor<Record<string, unknown>>;
}

export interface AppHeaderProps {
  /**
   * If `true`, show menu button as if menu is expanded, otherwise show it as if menu is collapsed.
   */
  menuOpen: boolean;
  /**
   * Callback fired when the menu button is clicked.
   */
  onToggleMenu: (open: boolean) => void;
  /**
   * Whether the menu icon should always be hidden.
   * @default false
   */
  hideMenuButton?: boolean;
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: AppHeaderSlots;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: AppHeaderSlotProps;
}

function AppHeader(props: AppHeaderProps): React.JSX.Element {
  const {
    menuOpen,
    onToggleMenu,
    hideMenuButton,
    slots,
    slotProps,
  } = props;

  const handleToggleMenu = React.useCallback(() => {
    onToggleMenu(!menuOpen);
  }, [menuOpen, onToggleMenu]);

  const sidebarExpansion = React.useContext(SidebarExpansionContext);

  const getMenuIcon = React.useCallback(
    (isExpanded: boolean) => {
      const expandMenuActionText = 'Expand';
      const collapseMenuActionText = 'Collapse';

      return (
        <TooltipIconButton
          tooltip={`${isExpanded ? collapseMenuActionText : expandMenuActionText} menu`}
          aria-label={`${isExpanded ? collapseMenuActionText : expandMenuActionText} navigation menu`}
          onClick={handleToggleMenu}
          enterDelay={1000}
        >
          {isExpanded ? <MenuOpenIcon /> : <MenuIcon />}
        </TooltipIconButton>
      );
    },
    [handleToggleMenu],
  );

  const ToolbarActionsSlot = slots?.toolbarActions ?? (() => null);

  return (
    <AppBar color="inherit" position="absolute" sx={{ displayPrint: 'none' }}>
      <Toolbar sx={{ backgroundColor: 'inherit', minHeight: '46px !important', mx: { xs: -0.75, sm: -1 } }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: 'wrap',
            width: '100%'
          }}>
          <Stack direction="row" sx={{
            alignItems: "center"
          }}>
            {/* Desktop menu button - left side */}
            {!hideMenuButton ? (
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  mr: 1,
                }}
              >
                {getMenuIcon(menuOpen)}
              </Box>
            ) : null}
            {sidebarExpansion ? (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  mr: 1,
                }}
              >
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 20, alignSelf: 'center', mr: 0.5 }}
                />
                <TooltipIconButton
                  tooltip="Collapse all"
                  aria-label="Collapse all sections"
                  onClick={sidebarExpansion.collapseAll}
                  disabled={!sidebarExpansion.anyExpanded}
                  enterDelay={1000}
                >
                  <UnfoldLessIcon sx={{ fontSize: 20 }} />
                </TooltipIconButton>
              </Box>
            ) : null}
            {slots?.appTitle ? <slots.appTitle {...slotProps?.appTitle} /> : null}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              marginLeft: 'auto'
            }}>
            <ToolbarActionsSlot {...slotProps?.toolbarActions} />
            {/* Mobile menu button - right side */}
            {!hideMenuButton ? (
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  ml: { sm: 1 },
                }}
              >
                {getMenuIcon(menuOpen)}
              </Box>
            ) : null}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export { AppHeader };

